import { Children, cloneElement, isValidElement } from 'react';
import type { ChangeEvent, ComponentProps, ReactNode } from 'react';

import type { TabVariant } from '@playbook/atoms/tab';
import { cn } from '@playbook/utils';

import css from './styles.module.css';

const DefaultTabGroupVariant: TabVariant = 'default' as const;

type TabGroupEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface TabGroupDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface TabGroupProperties
	extends TabGroupEventHandlers, TabGroupDataAttributes {
	/** Written onto every tab, so a row never mixes the two looks. */
	variant?: TabVariant;

	/** The `name` every tab shares, which is what makes them one choice. */
	name: string;
	/** Names the group for assistive technology, which sees no visible label. */
	label: string;

	/** For a Playbook entity that sets the group inside its own layout. */
	className?: string;

	/** Called with the checked tab's `value` whenever the selection moves. */
	onTabChange?: (value: string) => void;

	/** `Tab`s. Mark the starting tab with `defaultChecked`. */
	children: ReactNode;
}

/**
 *	Lays tabs out in one row and makes them one radio group, with the checked
 *	tab's `panel` below the row. It holds no state: the browser keeps which
 *	radio is checked, CSS shows the panel after it, and the group listens for
 *	the change as it bubbles up from the tabs.
 *
 *	The group owns `name` and `variant` and writes both onto every tab,
 *	warning rather than silently winning when a caller set one on a child.
 */
export function TabGroup({
	variant = DefaultTabGroupVariant,

	name,
	label,

	className,

	onTabChange,

	children,

	onChange,
	...props
}: TabGroupProperties): ReactNode {
	let hasPanels = false;

	const tabs: ReactNode = Children.map(children, (child) => {
		if (
			!isValidElement<{
				name?: string;
				variant?: TabVariant;
				panel?: ReactNode;
			}>(child)
		) {
			return child;
		}

		if (child.props.panel !== undefined) {
			hasPanels = true;
		}

		if (child.props.name !== undefined) {
			console.warn(
				'TabGroup sets `name` for every tab. Set it on the group; the one on its children is ignored.'
			);
		}

		if (child.props.variant !== undefined) {
			console.warn(
				'TabGroup sets `variant` for every tab. Set it on the group; the one on its children is ignored.'
			);
		}

		return cloneElement(child, { name, variant });
	});

	const handleChange = (event: ChangeEvent<HTMLDivElement>): void => {
		onChange?.(event);

		const field: EventTarget = event.target;

		// Only this group's own radios move the selection. A field inside a
		// panel bubbles its change up through here too.
		if (field instanceof HTMLInputElement && field.name === name) {
			onTabChange?.(field.value);
		}
	};

	return (
		<div
			{...props}
			role="radiogroup"
			aria-label={label}
			className={cn(css.group, !hasPanels && css.scrollable, className)}
			onChange={handleChange}>
			{tabs}
		</div>
	);
}
